import type { LocationScheduledDayFilter } from './quick-filters'
import type { Action, FormValues } from './types'
import type {
  Holiday,
  ListQuerySortField,
  ListQuerySortOrder,
  ListState,
  Location,
  PremiumAchPull,
  PremiumGroup,
  PremiumGroupInvoice,
  PremiumInvoiceStatus,
  QueryFilter,
  SearchParams
} from '../types'
import type { FormInstance, TableColumnsType } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { ZodIssue } from 'zod'

import {
  CalendarOutlined,
  CaretDownOutlined,
  CheckOutlined,
  CloseOutlined,
  DeleteFilled,
  DollarCircleOutlined,
  FileExclamationOutlined,
  SearchOutlined,
  SyncOutlined
} from '@ant-design/icons'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Button, Col, Collapse, Dropdown, Form, Input, Row, Space, Table, Tag, Tooltip } from 'antd'
import { AxiosError, isAxiosError } from 'axios'
import cx from 'classnames'
import {
  addDays,
  addMonths,
  endOfMonth,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  isValid,
  parseISO,
  setDate,
  startOfDay,
  startOfMonth
} from 'date-fns'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useReducer, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useDebounce, useInterval } from 'react-use'

import $api from '../../../API'
import { InvoiceProcessingLevel } from '../../../domain/invoice-processing-level.enum'
import { PushType, mapper as pushTypeNameMapper } from '../../../domain/push-type.enum'
import { CellTitle } from '../components/cell-title'
import { EmptyCellValue } from '../components/empty-cell-value'
import { ExpandIcon } from '../components/expand-icon'
import { InvoiceLines } from '../components/invoice-lines'
import { currency, duration, ordinal } from '../formatters'
import { PremiumInvoiceStatusMapper } from '../types'
import { round } from '../utils'

import { FundingInformation } from './funding-information'
import { GeneralInformation } from './general-information'
import { GroupAccount } from './group-account/group-account'
import { PinnacleInformation } from './pinnacle-information'
import { PremiumGroupsFooter } from './premium-groups-footer'
import { QuickFilters } from './quick-filters'

import './index.css'

const initialState: ListState = {
  expandedRow: {
    id: undefined
  },

  searchParams: {
    query: '',
    locationIds: [],
    fundingTypes: [],
    pushTypes: [],
    networks: [],
    openAr: [],
    currentAr: [],
    schedule: [],
    isActive: [],
    flags: [],
    nextPullDate: []
  },

  pagination: {
    pageSize: 10,
    currentPage: 0
  },

  sort: {
    field: undefined,
    order: undefined
  }
}

function reducer(state: ListState, action: Action): ListState {
  switch (action.type) {
    case 'set-query': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          query: action.payload
        }
      }
    }

    case 'set-expanded-row-id': {
      return {
        ...state,
        expandedRow: {
          ...state.expandedRow,
          id: action.payload
        }
      }
    }

    case 'clear-expanded-row-id': {
      return {
        ...state,
        expandedRow: {
          ...state.expandedRow,
          id: undefined
        }
      }
    }

    case 'set-pagination': {
      return {
        ...state,
        pagination: action.payload
      }
    }

    case 'set-sort': {
      return {
        ...state,
        sort: action.payload,
        pagination: {
          ...state.pagination,
          currentPage: 0
        }
      }
    }

    case 'set-refresh-interval': {
      return {
        ...state,
        refreshInterval: action.payload
      }
    }

    case 'clear-refresh-interval': {
      return {
        ...state,
        refreshInterval: undefined
      }
    }

    case 'clear-filters': {
      return {
        ...state,
        searchParams: initialState.searchParams,
        pagination: {
          ...state.pagination,
          currentPage: 0
        }
      }
    }

    case 'set-filters': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          ...action.payload
        },
        pagination: {
          ...state.pagination,
          currentPage: 0
        }
      }
    }

    default: {
      return state
    }
  }
}

type MutationPayload = Pick<
  PremiumGroup,
  | 'id'
  | 'pinnacle_account_number'
  | 'pinnacle_routing_number'
  | 'premium_account_number'
  | 'premium_funding_type'
  | 'premium_is_active'
  | 'premium_routing_number'
  | 'premium_schedule_day'
  | 'invoice_processing_level'
>

interface QueryDataFilters {
  premium_funding_type: QueryFilter[]
  premium_schedule_day: QueryFilter[]
  premium_is_active: QueryFilter[]
  open_ar: QueryFilter[]
  current_ar: QueryFilter[]
  location: QueryFilter[]
  flags: QueryFilter[]
  next_pull_date: QueryFilter[]
  location_schedule_day: LocationScheduledDayFilter[]
}

interface QueryData {
  groups: PremiumGroup[]
  totalFiltered: number
  totalUnfiltered: number
  filters: QueryDataFilters
  divisions: string[]
  billingTypes: string[]
}

type QueryContextKey = [
  'groups',
  ListState['sort'],
  ListState['pagination'],
  ListState['searchParams']
]

type MutationError = ValidationError | Error

class ValidationError extends Error {
  issues: Record<string, string>

  constructor(issues: ZodIssue[]) {
    super('Validation error')
    this.issues = Object.fromEntries(issues.map(issue => [issue.path.join('.'), issue.message]))
  }
}

const initialValues: FormValues = {
  name: '',
  number: '',
  premium_is_active: false,
  premium_level_funded: false,
  current_ar: 0,
  open_ar: 0,
  invoice_processing_level: InvoiceProcessingLevel.Group
}

export const baseCellClassName = 'text-xs font-lato'
export const baseHeaderCellClassName = 'text-[0.675rem] uppercase font-lato !bg-transparent'

export function PremiumPaymentsGroupManagementPage(): JSX.Element {
  const [form] = Form.useForm<FormValues>()
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [selectedRows, setSelectedRows] = useState<PremiumGroup[]>([])
  const [sp, setSP] = useSearchParams()

  useEffect(() => {
    form.setFieldsValue(initialValues)
  }, [form])

  useEffect(() => {
    dispatch({
      type: 'set-query',
      payload: sp.get('query') ?? ''
    })
    dispatch({
      type: 'set-pagination',
      payload: {
        pageSize: Number(sp.get('page-size') ?? 10),
        currentPage: Number(sp.get('page') ?? 0)
      }
    })

    const field = sp.get('field') as ListQuerySortField
    const order = sp.get('order') as ListQuerySortOrder
    console.info(field, order)
    if (order && field) {
      dispatch({
        type: 'set-sort',
        payload: {
          field,
          order
        }
      })
    }
  }, [sp])

  const [state, dispatch] = useReducer(reducer, initialState, state => {
    return state
  })

  const [debouncedQuery, setDebouncedQuery] = useState(state.searchParams.query)

  useDebounce(
    () => {
      const query = (state.searchParams.query ?? '').trim().toLowerCase()

      setSP(params => {
        params.set('query', query)
        return params
      })
      setDebouncedQuery(query)
      dispatch({ type: 'set-pagination', payload: { ...state.pagination, currentPage: 0 } })
    },
    500,
    [state.searchParams.query]
  )

  useDebounce(
    () => {
      setSP(params => {
        params.set('page', state.pagination.currentPage.toString(10))
        params.set('page-size', state.pagination.pageSize.toString(10))
        params.set('field', state.sort.field ?? '')
        params.set('order', state.sort.order ?? '')
        return params
      })
    },
    500,
    [state.pagination, state.sort]
  )

  const query = useQuery<unknown, unknown, QueryData, QueryContextKey>({
    queryKey: [
      'groups',
      state.sort,
      state.pagination,
      { ...state.searchParams, query: debouncedQuery }
    ],

    refetchOnMount: true,

    async queryFn(context) {
      const [, sort, pagination, searchParams] = context.queryKey
      const { pageSize, currentPage } = pagination
      const {
        query = '',
        locationIds,
        fundingTypes,
        currentAr,
        openAr,
        schedule,
        isActive,
        flags,
        nextPullDate
      } = searchParams

      const searchFields = [
        'name',
        'number',
        'premium_routing_number',
        'premium_account_number',
        'group_originator_id',
        'group_banking_id',
        'claims_account_number'
      ]

      const sp = new URLSearchParams([
        ...(currentPage > 0 ? [['page', currentPage.toString(10)]] : []),
        ['page-size', pageSize.toString(10)],
        ...(typeof sort.field === 'string' ? [['order-by', sort.field]] : []),
        ...(typeof sort.order === 'string' ? [['direction', sort.order]] : []),
        ...(locationIds.length > 0 ? locationIds.map(id => ['location-id', id]) : []),
        ...(fundingTypes.length > 0 ? fundingTypes.map(ft => ['premium-funding-type', ft]) : []),
        ...(currentAr.length > 0 ? currentAr.map(ar => ['current-ar', ar.toString(10)]) : []),
        ...(openAr.length > 0 ? openAr.map(ar => ['open-ar', ar.toString(10)]) : []),
        ...(schedule.length > 0
          ? schedule.map(day => ['premium-schedule-day', day.toString(10)])
          : []),
        ...(isActive.length > 0
          ? isActive.map(active => ['premium-is-active', active.toString()])
          : []),
        ...(query.length > 0
          ? [['query', query], ...searchFields.map(name => ['search-fields', name])]
          : []),
        ...flags.map(flag => ['flag', flag]),
        ...nextPullDate.map(date => ['next-pull-date', date]),
        ['is_premium', 'true']
      ])

      const { data } = await $api.get(`/groups?${sp.toString()}`)

      return {
        groups: data.rows,

        totalFiltered: data.total_filtered,

        totalUnfiltered: data.total_unfiltered,

        filters: data.filters,

        divisions: data.divisions,

        billingTypes: data.billing_types
      }
    },

    onSuccess() {
      dispatch({ type: 'clear-expanded-row-id' })
    },

    keepPreviousData: true
  })

  const mutation = useMutation<unknown, MutationError, MutationPayload>({
    async mutationFn({ id, ...data }: MutationPayload) {
      const isNew = id === 'new'

      const url = isNew ? '/groups' : `/groups/${id}`

      const response = await $api.request({
        url,
        method: isNew ? 'POST' : 'PATCH',
        data
      })

      if (response.status !== 200) {
        if (response.status === 422) {
          throw new ValidationError(response.data)
        }

        throw new Error('Failed to update group')
      }

      dispatch({ type: 'clear-expanded-row-id' })
      void query.refetch()
    }
  })

  const locationsQuery = useQuery<unknown, unknown, Location[]>({
    queryKey: ['locations'],

    refetchOnMount: true,

    async queryFn() {
      try {
        const response = await $api.get('/locations')
        return response.data
      } catch (e) {
        if (e instanceof AxiosError) {
          throw new TypeError(`Request failed with status ${e.status} (${e.message})`)
        }
      }
    }
  })

  const holidaysQuery = useQuery<unknown, unknown, Holiday[]>({
    queryKey: ['holidays'],

    async queryFn() {
      const response = await $api.get('/holidays')

      return response.data
    }
  })

  const pullsQuery = useQuery<unknown, unknown, { rows: PremiumAchPull[] }>({
    queryKey: ['pulls'],

    async queryFn() {
      const sp = new URLSearchParams([
        ['status', 'initial_review'],
        ['page-size', '999999']
      ])

      try {
        const response = await $api.get(`/batches?${sp.toString()}`)

        return response.data
      } catch (e) {
        if (isAxiosError(e)) {
          throw new Error(`Request failed with status ${e.status} (${e.message})`)
        }
      }
    }
  })

  const columns = useMemo<TableColumnsType<PremiumGroup>>(
    () => [
      {
        title({ sortColumns }) {
          return (
            <CellTitle id='number' sortColumns={sortColumns}>
              Group #
            </CellTitle>
          )
        },
        key: 'number',
        dataIndex: 'number',
        sorter: true,
        sortOrder:
          state.sort.field === 'number'
            ? state.sort.order === 'asc'
              ? 'ascend'
              : 'descend'
            : undefined,
        showSorterTooltip: false,
        width: '12%',
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) })
      },
      {
        title({ sortColumns }) {
          return (
            <CellTitle id='name' sortColumns={sortColumns}>
              Name
            </CellTitle>
          )
        },
        key: 'name',
        dataIndex: 'name',
        sorter: true,
        sortOrder:
          state.sort.field === 'name'
            ? state.sort.order === 'asc'
              ? 'ascend'
              : 'descend'
            : undefined,
        showSorterTooltip: false,
        width: '15%',
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) })
      },
      {
        title: 'Location',
        key: 'location',
        dataIndex: ['location', 'short_name'],
        width: '7%',
        align: 'center',

        filters: locationsQuery.data
          ?.map(location => {
            const filter = query.data?.filters.location.find(
              filter => filter.value === location.id
            ) ?? { count: 0, value: location.id }

            return {
              ...filter,
              name: location.name,
              short_name: location.short_name
            }
          })
          .map(filter => ({
            text: (
              <div
                className={cx(
                  'flex justify-between items-center gap-6',
                  (filter.value === null || filter.count === 0) && 'text-gray-400'
                )}
              >
                <span>
                  {filter.name} <span className='text-gray-500'>({filter.short_name})</span>
                </span>

                <span>{filter.count}</span>
              </div>
            ),
            value: filter.value as string
          })),

        filterMultiple: true,
        filteredValue: state.searchParams.locationIds,

        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) })
      },
      {
        title: 'Active',
        key: 'premium_is_active',
        dataIndex: 'premium_is_active',
        render: BooleanCell,
        width: '7%',
        align: 'center',

        filters: [true, false]
          .map(
            value =>
              query.data?.filters.premium_is_active.find(filter => filter.value === value) ?? {
                count: 0,
                value
              }
          )
          .map(filter => ({
            text: (
              <div
                className={cx(
                  'flex justify-between items-center gap-6',
                  (filter.value === null || filter.count === 0) && 'text-gray-400'
                )}
              >
                <span>
                  {typeof filter.value === 'boolean'
                    ? filter.value
                      ? 'Yes'
                      : 'No'
                    : filter.value === null
                      ? '(Empty)'
                      : filter.value}
                </span>

                <span>{filter.count}</span>
              </div>
            ),
            value: filter.value ?? 'null'
          })),

        filterMultiple: true,
        filteredValue: state.searchParams.isActive,

        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) })
      },
      {
        title({ sortColumns }) {
          return (
            <CellTitle id='current_ar' sortColumns={sortColumns}>
              Current A/R
            </CellTitle>
          )
        },
        key: 'current_ar',
        dataIndex: 'current_ar',
        align: 'right',
        sorter: true,
        sortOrder:
          state.sort.field === 'current_ar'
            ? state.sort.order === 'asc'
              ? 'ascend'
              : 'descend'
            : undefined,
        showSorterTooltip: false,

        filters: [0, 'positive', 'negative', null]
          .map(
            value =>
              query.data?.filters.current_ar.find(filter => filter.value === value) ?? {
                count: 0,
                value
              }
          )
          .map(filter => ({
            text: (
              <div
                className={cx(
                  'flex justify-between items-center gap-6',
                  (filter.value === null || filter.count === 0) && 'text-gray-400'
                )}
              >
                <span>
                  {typeof filter.value === 'number'
                    ? currency(filter.value)
                    : filter.value === 'negative'
                      ? `< ${currency(0)}`
                      : filter.value === 'positive'
                        ? `> ${currency(0)}`
                        : filter.value === null
                          ? '(Empty)'
                          : filter.value}
                </span>

                <span>{filter.count}</span>
              </div>
            ),
            value: filter.value ?? 'null'
          })),

        filterMultiple: true,
        filteredValue: state.searchParams.currentAr,

        render(value: number | null | undefined) {
          if (typeof value !== 'number') {
            return <EmptyCellValue />
          }

          return <span>{currency(value ?? 0)}</span>
        },
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) })
      },
      {
        title({ sortColumns }) {
          return (
            <CellTitle id='open_ar' sortColumns={sortColumns}>
              Open A/R
            </CellTitle>
          )
        },
        key: 'open_ar',
        dataIndex: 'open_ar',
        align: 'right',
        sorter: true,
        sortOrder:
          state.sort.field === 'open_ar'
            ? state.sort.order === 'asc'
              ? 'ascend'
              : 'descend'
            : undefined,
        showSorterTooltip: false,

        filters: [0, 'positive', 'negative', null]
          .map(
            value =>
              query.data?.filters.open_ar.find(filter => filter.value === value) ?? {
                count: 0,
                value
              }
          )
          .map(filter => ({
            text: (
              <div
                className={cx(
                  'flex justify-between items-center gap-6',
                  (filter.value === null || filter.count === 0) && 'text-gray-400'
                )}
              >
                <span>
                  {typeof filter.value === 'number'
                    ? currency(filter.value)
                    : filter.value === 'negative'
                      ? `< ${currency(0)}`
                      : filter.value === 'positive'
                        ? `> ${currency(0)}`
                        : filter.value === null
                          ? '(Empty)'
                          : filter.value}
                </span>

                <span>{filter.count}</span>
              </div>
            ),
            value: filter.value ?? 'null'
          })),

        filterMultiple: true,
        filteredValue: state.searchParams.openAr,

        render(value: number | null | undefined) {
          if (typeof value !== 'number') {
            return <EmptyCellValue />
          }

          return <span>{currency(value ?? 0)}</span>
        },
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) })
      },
      {
        title: 'Funding Type',
        key: 'premium_funding_type',
        dataIndex: 'premium_funding_type',
        align: 'center',

        filters: query.data?.filters.premium_funding_type
          .filter(filter => filter.value !== PushType.Check)
          .map(filter => ({
            text: (
              <div
                className={cx(
                  'flex justify-between items-center gap-6',
                  filter.value === null && 'text-gray-400'
                )}
              >
                <span>
                  {filter.value === null
                    ? '(Empty)'
                    : typeof filter.value === 'string'
                      ? (pushTypeNameMapper[filter.value as PushType] ?? filter.value)
                      : filter.value}
                </span>

                <span>{filter.count}</span>
              </div>
            ),
            value: filter.value ?? 'null'
          })),

        filterMultiple: true,
        filteredValue: state.searchParams.fundingTypes,

        render(value) {
          return pushTypeNameMapper[value as PushType] ?? value ?? <EmptyCellValue />
        },
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) })
      },
      {
        title: 'Schedule',
        key: 'premium_schedule_day',
        dataIndex: 'premium_schedule_day',
        align: 'center',

        filters: Array.from({ length: 31 }, (_, k) => k + 1)
          .concat(null as never)
          .map(
            value =>
              query.data?.filters.premium_schedule_day.find(filter => filter.value === value) ?? {
                count: 0,
                value
              }
          )
          .map(filter => ({
            text: (
              <div
                className={cx(
                  'flex justify-between items-center gap-6',
                  (filter.value === null || filter.count === 0) && 'text-gray-400'
                )}
              >
                <span>
                  {typeof filter.value === 'number'
                    ? ordinal(filter.value)
                    : filter.value === null
                      ? '(Empty)'
                      : filter.value}
                </span>

                <span>{filter.count}</span>
              </div>
            ),
            value: filter.value ?? 'null'
          })),

        filterMultiple: true,
        filteredValue: state.searchParams.schedule,

        render(value) {
          if (!value) {
            return <EmptyCellValue />
          }

          return ordinal(value)
        },
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) })
      },
      {
        title: 'Pull Date',
        key: 'next_pull_date',
        dataIndex: 'next_pull_date',
        align: 'center',

        filters: query.data?.filters.next_pull_date.map(filter => ({
          text: (
            <div
              className={cx(
                'flex justify-between items-center gap-6',
                filter.value === null && 'text-gray-400'
              )}
            >
              <span>
                {filter.value === null
                  ? '(Empty)'
                  : typeof filter.value === 'string'
                    ? format(parseISO(filter.value), 'MM/dd/yyyy')
                    : filter.value}
              </span>

              <span>{filter.count}</span>
            </div>
          ),
          value: filter.value ?? 'null'
        })),

        filterMultiple: true,
        filteredValue: state.searchParams.nextPullDate,

        render(value) {
          if (!value) {
            return <EmptyCellValue />
          }

          const date = parseISO(value)

          return <span title={format(date, 'EEEE')}>{date.toLocaleDateString()}</span>
        },
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) })
      },
      {
        title: 'Flags',
        key: 'flags',
        dataIndex: 'flags',
        align: 'center',

        filters: flags.map(flag => {
          const count = query.data?.filters.flags.find(filter => filter.value === flag.value)?.count

          return {
            value: flag.value,
            text: (
              <div className={cx('flex gap-6 justify-between items-center')}>
                <span>{flag.text}</span>

                {typeof count === 'number' && <span className='text-gray-400'>{count}</span>}
              </div>
            )
          }
        }),
        filterMultiple: true,
        filteredValue: state.searchParams.flags,

        render(value: string[]) {
          if (value.length === 0) {
            return <EmptyCellValue />
          }

          return (
            <Space>
              {value.map(v => {
                const flag = flags.find(flag => flag.value === v)

                if (!flag) {
                  return null
                }

                return (
                  <Tooltip title={flag.text} key={flag.value}>
                    <flag.icon className={flag.className} />
                  </Tooltip>
                )
              })}
            </Space>
          )
        },
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) })
      }
    ],
    [
      locationsQuery.data,
      query.data?.filters,
      state.searchParams,
      state.sort.field,
      state.sort.order
    ]
  )

  const handleSave = useCallback(() => {
    const { id } = state.expandedRow
    const values = form.getFieldsValue()

    if (id === undefined) {
      return
    }

    mutation.mutate({
      id,
      premium_routing_number: values.premium_routing_number?.trim().replaceAll(/\D/g, '') || null,
      premium_account_number: values.premium_account_number?.trim() || null,
      pinnacle_routing_number: values.pinnacle_routing_number?.trim().replaceAll(/\D/g, '') || null,
      pinnacle_account_number: values.pinnacle_account_number?.trim() || null,
      premium_funding_type: values.premium_funding_type ?? null,
      premium_schedule_day: values.premium_schedule_day ?? null,
      premium_is_active: values.premium_is_active ?? false,
      invoice_processing_level: values.invoice_processing_level
    })
  }, [form, mutation, state.expandedRow])

  const handleCancel = useCallback(() => {
    form.setFieldsValue(initialValues)
    dispatch({ type: 'clear-expanded-row-id' })
  }, [form])

  const clearSelection = useCallback(() => {
    setSelectedRowKeys([])
    setSelectedRows([])
  }, [])

  const handleFilterAssistant = useCallback((filter: Partial<SearchParams>) => {
    dispatch({
      type: 'set-filters',
      payload: Object.assign(
        {
          locationIds: [],
          schedule: [],
          fundingTypes: [],
          pushTypes: [],
          networks: [],
          openAr: [],
          currentAr: [],
          isActive: [],
          flags: [],
          nextPullDate: []
        },
        filter
      )
    })
  }, [])

  function handleRowExpand(expanded: boolean, record: PremiumGroup): void {
    if (!expanded) {
      form.setFieldsValue(initialValues)
      dispatch({ type: 'clear-expanded-row-id' })
      return
    }

    form.setFieldsValue({
      name: record.name,
      number: record.number,
      location_id: record.location.id,

      effective_date: record.effective_date ? dayjs(record.effective_date) : undefined,
      renewal_date: record.renewal_date ? dayjs(record.renewal_date) : undefined,
      termination_date: record.termination_date ? dayjs(record.termination_date) : undefined,
      run_out_date: record.run_out_date ? dayjs(record.run_out_date) : undefined,

      premium_account_number: record.premium_account_number ?? '',
      premium_routing_number: record.premium_routing_number ?? '',
      pinnacle_account_number: record.pinnacle_account_number ?? '',
      pinnacle_routing_number: record.pinnacle_routing_number ?? '',
      premium_funding_type: record.premium_funding_type ?? undefined,
      premium_schedule_day: record.premium_schedule_day ?? undefined,
      premium_is_active: record.premium_is_active ?? false,
      premium_level_funded: record.release_type === 'level-funded',

      current_ar: record.current_ar ?? 0,
      open_ar: record.open_ar ?? 0,
      invoice_processing_level: record.invoice_processing_level
    })

    dispatch({ type: 'set-expanded-row-id', payload: record.id })
  }

  const isDefaultFilters = useMemo(() => {
    return !Object.values(state.searchParams).some(value => value.length > 0)
  }, [state.searchParams])

  useInterval(() => {
    void query.refetch()
  }, state.refreshInterval ?? null)

  const onSelectChange = (newSelectedRowKeys: React.Key[], newSelectedRows: PremiumGroup[]) => {
    setSelectedRowKeys(newSelectedRowKeys)
    setSelectedRows(newSelectedRows)
  }

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange
  }

  return (
    <>
      <div className='flex flex-col gap-4 p-6 font-normal rounded-lg font-lato bg-lucent-background text-lucent-text'>
        <div className='flex justify-between items-start'>
          <div className='flex gap-6 items-center'>
            <h1 className='text-2xl'>Premium Payments Group Management</h1>
          </div>
        </div>

        <div className='flex flex-col rounded-lg border'>
          <div className='flex flex-col'>
            <div className='flex flex-row gap-2 items-center p-4'>
              <div className='flex-grow'>
                <Input
                  placeholder='Search groups'
                  className='font-lato text-sm py-1 bg-gray-50 [&_input]:bg-transparent rounded [&_input]:placeholder:text-gray-400'
                  prefix={<SearchOutlined className='text-gray-500' />}
                  suffix={
                    (state.searchParams.query.length ?? 0) > 0 ? (
                      <button
                        type='button'
                        className='flex justify-center items-center p-2 -m-2 border'
                        onClick={() => {
                          dispatch({
                            type: 'set-query',
                            payload: ''
                          })
                        }}
                      >
                        <CloseOutlined size={16} className='text-gray-500' />
                      </button>
                    ) : undefined
                  }
                  value={state.searchParams.query ?? ''}
                  onChange={event => {
                    dispatch({
                      type: 'set-query',
                      payload: event.target.value
                    })
                  }}
                />
              </div>

              <Space>
                {!isDefaultFilters && (
                  <Button
                    type='default'
                    onClick={() => {
                      dispatch({
                        type: 'clear-filters'
                      })
                    }}
                  >
                    <span className='mr-2 -translate-y-[4px]'>
                      <DeleteFilled className='text-lucent-primary' />
                    </span>
                    <span className='font-lato font-bold text-lucent-primary text-[10px] uppercase -translate-y-[1px]'>
                      Clear Filters
                    </span>
                  </Button>
                )}

                <Dropdown.Button
                  type='default'
                  disabled={query.isFetching}
                  onClick={() => {
                    void query.refetch()
                  }}
                  icon={
                    <CaretDownOutlined
                      className={cx(
                        'w-[12px] h-[12px] !translate-y-[2px]',
                        typeof state.refreshInterval === 'number' && 'text-lucent-primary',
                        typeof state.refreshInterval !== 'number' && 'text-lucent-gray'
                      )}
                    />
                  }
                  menu={{
                    items: [30, 60, 60 * 5, 60 * 30].map(seconds => ({
                      key: `refresh-${seconds}`,
                      itemIcon() {
                        if (state.refreshInterval === seconds * 1000) {
                          return <CheckOutlined className='ml-2 text-lucent-primary' />
                        }

                        return null
                      },
                      onClick() {
                        if (state.refreshInterval === seconds * 1000) {
                          dispatch({
                            type: 'clear-refresh-interval'
                          })

                          return
                        }

                        dispatch({
                          type: 'set-refresh-interval',
                          payload: seconds * 1000
                        })
                      },
                      label: duration(seconds * 1000)
                    }))
                  }}
                >
                  <div className='flex items-center space-x-1.5'>
                    <SyncOutlined
                      className={cx(
                        query.isFetching && 'animate-custom-spin text-lucent-gray',
                        !query.isFetching && 'text-lucent-primary'
                      )}
                    />

                    <span
                      className={cx(
                        'font-lato font-bold text-[10px] uppercase',
                        query.isFetching && 'text-lucent-gray',
                        !query.isFetching && 'text-lucent-primary'
                      )}
                    >
                      <span>Refresh</span>

                      {typeof state.refreshInterval === 'number' && (
                        <span className=''>
                          <span className='mx-1.5'>|</span>
                          Auto {duration(state.refreshInterval, { short: true })}
                        </span>
                      )}
                    </span>
                  </div>
                </Dropdown.Button>
              </Space>
            </div>
          </div>

          {locationsQuery.data !== undefined &&
            query.data?.filters.location_schedule_day !== undefined && (
              <div className='flex flex-col'>
                <QuickFilters
                  filters={query.data?.filters.location_schedule_day}
                  locations={locationsQuery.data}
                  currentSearchParams={state.searchParams}
                  onClick={handleFilterAssistant}
                />
              </div>
            )}

          <Table
            size='small'
            columns={columns}
            rowKey='id'
            dataSource={query.data?.groups ?? []}
            loading={query.isFetching}
            className='rounded-none border-t payment-files-table'
            rowSelection={rowSelection}
            expandable={{
              expandedRowKeys:
                typeof state.expandedRow.id === 'string' ? [state.expandedRow.id] : [],
              expandedRowRender(group) {
                return (
                  <ExpandedRow
                    group={group}
                    form={form}
                    isSubmitting={mutation.isLoading}
                    holidays={holidaysQuery.data ?? []}
                    availableLocations={locationsQuery.data ?? []}
                    error={mutation.error ?? undefined}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    billingTypes={query.data?.billingTypes}
                  />
                )
              },
              expandIcon: ExpandIcon,
              onExpand: handleRowExpand
            }}
            onRow={record => {
              return {
                className: cx({
                  'bg-purple-50 !hover:bg-purple-50': record.open_ar !== record.current_ar
                })
              }
            }}
            pagination={{
              current: state.pagination.currentPage + 1,
              pageSize: state.pagination.pageSize,
              pageSizeOptions: ['10', '25', '50', '100'],
              showSizeChanger: true,
              total: query.data?.totalFiltered ?? 0,
              showTotal(total, [from, to]) {
                const parts = [
                  `Showing ${from}–${to} of ${total} ${total === 1 ? 'item' : 'items'}`
                ]

                if (
                  typeof query.data?.totalUnfiltered === 'number' &&
                  query.data.totalUnfiltered !== total
                ) {
                  parts.push(
                    `Total ${query.data.totalUnfiltered} ${query.data.totalUnfiltered === 1 ? 'item' : 'items'}`
                  )
                }

                return <span className='text-sm text-gray-500 font-lato'>{parts.join(' • ')}</span>
              },
              hideOnSinglePage: false
            }}
            onChange={(pagination, filters, sorter, extra) => {
              if (extra.action === 'paginate') {
                dispatch({
                  type: 'set-pagination',
                  payload: {
                    pageSize: pagination.pageSize ?? 10,
                    currentPage: pagination.current ? pagination.current - 1 : 0
                  }
                })
              }

              if (extra.action === 'sort') {
                const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter

                dispatch({
                  type: 'set-sort',
                  payload: {
                    field: singleSorter?.column?.key as ListQuerySortField | undefined,
                    order:
                      typeof singleSorter?.order === 'string'
                        ? ({ ascend: 'asc', descend: 'desc' }[
                            singleSorter.order
                          ] as ListQuerySortOrder)
                        : undefined
                  }
                })
              }

              if (extra.action === 'filter') {
                dispatch({
                  type: 'set-filters',
                  payload: {
                    locationIds: (filters.location ??
                      []) as ListState['searchParams']['locationIds'],
                    fundingTypes: (filters.premium_funding_type ??
                      []) as ListState['searchParams']['fundingTypes'],
                    openAr: (filters.open_ar ?? []) as ListState['searchParams']['openAr'],
                    currentAr: (filters.current_ar ?? []) as ListState['searchParams']['currentAr'],
                    schedule: (filters.premium_schedule_day ??
                      []) as ListState['searchParams']['schedule'],
                    isActive: (filters.premium_is_active ??
                      []) as ListState['searchParams']['isActive'],
                    flags: (filters.flags ?? []) as ListState['searchParams']['flags'],
                    pushTypes: (filters.push_type ?? []) as ListState['searchParams']['pushTypes'],
                    networks: (filters.network ?? []) as ListState['searchParams']['networks'],
                    nextPullDate: (filters.next_pull_date ??
                      []) as ListState['searchParams']['nextPullDate']
                  }
                })
              }
            }}
          />
        </div>
        <PremiumGroupsFooter
          pulls={pullsQuery.data?.rows ?? []}
          selected={selectedRows}
          onClearSelection={clearSelection}
          holidays={holidaysQuery.data ?? []}
        />
      </div>
    </>
  )
}

function BooleanCell(value: boolean): JSX.Element | null {
  if (!value) {
    return null
  }

  return <CheckOutlined className='text-xs text-gray-700' />
}

interface GetNextPullDateOptions {
  scheduleDay: number
  holidays?: Holiday[]
  startDate?: Date
}

export const getNextPullDate = ({
  scheduleDay,
  holidays,
  startDate
}: GetNextPullDateOptions): Date => {
  const reference = startOfDay(startDate ?? new Date())

  if (!Array.from({ length: 31 }, (_, k) => k + 1).includes(scheduleDay)) {
    throw new Error('Invalid schedule day')
  }

  let c = 0
  let date = startOfDay(setDate(new Date(reference), scheduleDay))

  if (isBefore(date, reference)) {
    date = addMonths(date, 1)
  }

  while (true) {
    c++

    if (c >= 1e6) {
      throw new Error(
        `Failed to find next pull date: too many iterations (while searching, we've reached date ${date.toLocaleDateString()} and exhausted ${c} attempts, imagine that!)`
      )
    }

    const isWeekend = [0, 6].includes(date.getDay())
    const isHoliday = holidays?.some(holiday => isSameDay(date, parseISO(holiday.date)))
    const isOverridden = holidays
      ?.flatMap(holiday => holiday.overrides ?? [])
      .some(override => isSameDay(date, parseISO(override.date)))

    if ((!isWeekend && !isHoliday) || ((isWeekend || isHoliday) && isOverridden)) {
      break
    }

    date = addDays(date, 1)
  }

  return date
}

interface ExpandedRowProps {
  group: PremiumGroup
  form: FormInstance<FormValues>
  isSubmitting: boolean
  error?: Error
  availableLocations: Location[]
  holidays?: Holiday[]
  onSave: () => void
  onCancel: () => void
  billingTypes?: string[]
}

function ExpandedRow({
  group,
  form,
  isSubmitting,
  error,
  holidays,
  onSave,
  onCancel,
  billingTypes = []
}: ExpandedRowProps): JSX.Element {
  const validationError = error as Partial<ValidationError> | undefined
  const scheduleDay = Form.useWatch('premium_schedule_day', form)
  const fundingType = Form.useWatch('premium_funding_type', form)
  const [now, setNow] = useState(new Date())

  const nextPullDate = useMemo(() => {
    return typeof scheduleDay === 'number'
      ? getNextPullDate({ scheduleDay, holidays, startDate: now })
      : undefined
  }, [scheduleDay, holidays, now])

  const formattedNextPullDate =
    nextPullDate && isValid(nextPullDate)
      ? isSameDay(nextPullDate, now)
        ? 'today'
        : isSameDay(nextPullDate, addDays(now, 1))
          ? 'tomorrow'
          : nextPullDate.toLocaleDateString()
      : undefined

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date())
    }, 60_000)

    return () => {
      clearInterval(interval)
    }
  }, [])

  const columns = useMemo<ColumnsType<PremiumGroupInvoice>>(
    () => [
      {
        title: 'Invoice #',
        dataIndex: 'number',
        align: 'center',
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(
              'text-[0.675rem] uppercase font-lato !bg-transparent',
              baseHeaderCellClassName
            )
          }
        }
      },
      {
        title: 'Division',
        dataIndex: 'division',
        align: 'center',
        render(value) {
          if (typeof value !== 'string') {
            return <EmptyCellValue />
          }

          return value
        },
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(
              'text-[0.675rem] uppercase font-lato !bg-transparent',
              baseHeaderCellClassName
            )
          }
        }
      },
      {
        title: 'Date',
        dataIndex: 'billing_date',
        align: 'center',
        defaultFilteredValue: ['current-month'],
        filters: [
          {
            text: 'Current Month',
            value: 'current-month'
          },
          {
            text: 'Previous Months',
            value: 'previous-months'
          },
          {
            text: 'Future Months',
            value: 'future-months'
          }
        ],
        onFilter(value, record) {
          const date = parseISO(record.billing_date)
          const now = startOfDay(new Date())

          switch (value) {
            case 'current-month': {
              return isSameMonth(date, now)
            }

            case 'previous-months': {
              return isBefore(date, startOfMonth(now))
            }

            case 'future-months': {
              return isAfter(date, endOfMonth(now))
            }

            default: {
              return false
            }
          }
        },
        render(value) {
          if (typeof value !== 'string' || !isValid(parseISO(value))) {
            return <EmptyCellValue />
          }

          return format(parseISO(value), 'MM/dd/yyyy')
        },
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(
              'text-[0.675rem] uppercase font-lato !bg-transparent',
              baseHeaderCellClassName
            )
          }
        }
      },
      {
        title: 'Total',
        align: 'right',
        render(_, record) {
          const total = record.lines.reduce((acc, line) => acc + line.amount, 0)
          return currency(total)
        },
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(
              'text-[0.675rem] uppercase font-lato !bg-transparent',
              baseHeaderCellClassName
            )
          }
        }
      },
      {
        title: 'Balance due',
        align: 'right',
        filters: [
          {
            text: 'Non-zero',
            value: 'non-zero'
          },
          {
            text: '> $0.00',
            value: 'greater-than-zero'
          },
          {
            text: '< $0.00',
            value: 'less-than-zero'
          },
          {
            text: '$0.00',
            value: 0
          }
        ],
        onFilter(value, record) {
          const total = round(
            record.lines.reduce((acc, line) => acc + line.balance_due, 0),
            2
          )

          switch (value) {
            case 'non-zero': {
              return total !== 0
            }

            case 'greater-than-zero': {
              return total > 0
            }

            case 'less-than-zero': {
              return total < 0
            }

            case 0: {
              return total === 0
            }

            default: {
              return false
            }
          }
        },
        render(_, record) {
          const total = record.lines.reduce((acc, line) => acc + line.balance_due, 0)
          return currency(total)
        },
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(
              'text-[0.675rem] uppercase font-lato !bg-transparent',
              baseHeaderCellClassName
            )
          }
        }
      },
      {
        title: 'Status',
        align: 'center',
        dataIndex: 'status',
        render: (value: PremiumInvoiceStatus) => (
          <Tag
            color={PremiumInvoiceStatusMapper[value].color}
            title={PremiumInvoiceStatusMapper[value].name}
            className='font-lato size-xs'
          >
            {PremiumInvoiceStatusMapper[value].name}
          </Tag>
        ),
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(
              'text-[0.675rem] uppercase font-lato !bg-transparent',
              baseHeaderCellClassName
            )
          }
        }
      }
    ],
    []
  )

  return (
    <>
      <Form
        form={form}
        layout='vertical'
        className='p-6'
        colon={false}
        requiredMark={false}
        labelCol={{}}
        wrapperCol={{
          className: '-mt-1.5'
        }}
      >
        <Row gutter={32} justify='space-between'>
          <GeneralInformation group={group} />

          <Col span={10}>
            <FundingInformation
              formattedNextPullDate={formattedNextPullDate}
              fundingType={fundingType}
              nextPullDate={nextPullDate}
              validationError={validationError}
            />

            <GroupAccount group={group} billingTypes={billingTypes} form={form} error={error} />
          </Col>

          <PinnacleInformation validationError={validationError} fundingType={fundingType} />
        </Row>

        <Row className='pt-6'>
          <Col span={24} className='flex justify-end'>
            <Space>
              <Button
                size='middle'
                type='link'
                htmlType='reset'
                className='rounded-md'
                onClick={event => {
                  event.preventDefault()
                  onCancel()
                }}
              >
                Cancel
              </Button>

              <Button
                size='middle'
                type='primary'
                htmlType='submit'
                loading={isSubmitting}
                onClick={event => {
                  event.preventDefault()
                  onSave()
                }}
              >
                Save
              </Button>
            </Space>
          </Col>
        </Row>
      </Form>

      {(group?.invoices ?? []).length > 0 && (
        <Collapse ghost>
          <Collapse.Panel header='Invoices' key='invoices'>
            <Table
              size='small'
              rowKey='id'
              className='rounded-none payment-files-table [&_.ant-table]:![margin-inline:_0]'
              columns={columns}
              dataSource={group?.invoices ?? []}
              pagination={false}
              expandable={{
                expandIcon: ExpandIcon,
                showExpandColumn: false,
                expandRowByClick: true,
                expandedRowRender(invoice, _, __, expanded) {
                  if (!expanded) {
                    return null
                  }

                  return <InvoiceLines date={invoice.billing_date} lines={invoice.lines} />
                }
              }}
            />
          </Collapse.Panel>
        </Collapse>
      )}
    </>
  )
}

const flags = [
  {
    text: 'Expiring',
    value: 'is-expiring',
    icon: CalendarOutlined,
    className: 'text-orange-500'
  },
  {
    text: 'Expired',
    value: 'is-expired',
    icon: CalendarOutlined,
    className: 'text-red-500'
  },
  {
    text: 'Balance Due',
    value: 'balance-due',
    icon: DollarCircleOutlined,
    className: 'text-blue-500'
  },
  {
    text: 'Credit Balance',
    value: 'credit-balance',
    icon: DollarCircleOutlined,
    className: 'text-green-500'
  },
  {
    text: 'Outstanding Balance',
    value: 'outstanding-balance',
    icon: DollarCircleOutlined,
    className: 'text-red-500'
  },
  {
    text: 'Has Open Invoices',
    value: 'has-open-invoices',
    icon: FileExclamationOutlined,
    className: 'text-blue-500'
  }
]
